/* eslint-disable ember/no-computed-properties-in-native-classes */
import { A } from '@ember/array';
import type NativeArray from '@ember/array/-private/native-array';
import { action, computed, get, setProperties, set } from '@ember/object';
import { guidFor } from '@ember/object/internals';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import { isEmpty, isPresent, isBlank } from '@ember/utils';
import type { AsyncBelongsTo, AsyncHasMany } from '@ember-data/model';
import type StoreService from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { format, isAfter, isSameDay, parse, parseISO, startOfDay } from 'date-fns';
import type { DetailedChangeset } from 'ember-changeset/types';
import type { Task } from 'ember-concurrency';
import { didCancel, timeout, task, dropTask, restartableTask } from 'ember-concurrency';
import { pluralize } from 'ember-inflector';
import type AbstractFlowModel from 'garaje/models/abstract/abstract-flow';
import DropdownOption from 'garaje/models/dropdown-option';
import type FlowModel from 'garaje/models/flow';
import type GlobalFlowModel from 'garaje/models/global-flow';
import type InviteModel from 'garaje/models/invite';
import type LocationModel from 'garaje/models/location';
import type SignInFieldModel from 'garaje/models/sign-in-field';
import type SkinnyLocationModel from 'garaje/models/skinny-location';
import type SubscriptionModel from 'garaje/models/subscription';
import type UserDatum from 'garaje/models/user-datum';
import type SessionService from 'garaje/services/_private/session';
import { UnauthorizedError } from 'garaje/services/ajax/errors';
import type AjaxFetchService from 'garaje/services/ajax-fetch';
import type CurrentLocationService from 'garaje/services/current-location';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type LocationFeatureFlagsService from 'garaje/services/location-feature-flags';
import type MetricsService from 'garaje/services/metrics';
import type StateService from 'garaje/services/state';
import { DATE_FNS_MM_DD_YYYY } from 'garaje/utils/date-fns-tz-utilities';
import { convertToISO, validateDateString } from 'garaje/utils/date-format';
import type { EmployeeSearcherTask } from 'garaje/utils/employees-searcher';
import employeesSearcherTask from 'garaje/utils/employees-searcher';
import { DateFieldFormat, PURPOSE_OF_VISIT, SignInFieldKind } from 'garaje/utils/enums';
import type { ScopedSubLookup } from 'garaje/utils/mpb-location-eligibility';
import {
  isFlowEligibleForLocation,
  isLocationEligibleFromScopedMap,
  MPB_FEATURE_FLAG,
} from 'garaje/utils/mpb-location-eligibility';
import { sanitizeClipboardGrid } from 'garaje/utils/sanitize-clipboard-text';
import { createDateFieldValidator } from 'garaje/utils/spreadsheet-validation';
import urlBuilder from 'garaje/utils/url-builder';
import zft from 'garaje/utils/zero-for-tests';
import type Handsontable from 'handsontable';
import type { GridSettings } from 'handsontable';
import type { SinglePayload } from 'jsonapi/response';
import _uniq from 'lodash/uniq';
import moment from 'moment-timezone';
import Papa from 'papaparse';
import { cached, localCopy } from 'tracked-toolbox';
import { v4 as uuid } from 'uuid';

const ISO8601_DATE_FORMAT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}[-+]\d{2}:\d{2}$/;

// SQ-7414: when the backend defers a large bulk import it returns 202 + a queued
// status; the dashboard polls GET /api/v3/bulk-invites/:id until it finishes.
const BULK_INVITE_POLL_INTERVAL_MS = 2000;
const BULK_INVITE_MAX_POLLS = 150; // ~5 min ceiling before surfacing a timeout

type ExtendedGridSettings = GridSettings & {
  csvHeader: string;
  timeFormat?: string;
  datePickerConfig?: { minDate: Date };
  dateFormat?: DateFieldFormat | string;
};

interface HandsontableConfig {
  validators: { DateValidator: (this: ExtendedGridSettings, date: Date, cb: (isValid: boolean) => void) => void };
}

interface ValidationErrorForRow {
  row?: number;
  invalidRow?: number;
  error: string;
}

type BulkInviteStatus = 'queued' | 'running' | 'complete' | 'error';

type BulkInvitePayload = SinglePayload<{
  successRows?: number[];
  errorRows?: number[];
  bulkInviteId: string;
  // Present on async responses (202 + poll); absent on the synchronous 201.
  status?: BulkInviteStatus;
}>;

// In other places, `@ember/string/htmlSafe` is sufficient,
// but because we are forwarding these strings to another
// javascript library, we need to physically escape the string
const escapeHTML = (unsafeStr: string) => {
  return unsafeStr
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/\./g, '&#46;');
};

const DATE_FORMAT = 'MM/DD/YYYY';
const TIME_FORMAT = 'h:mm a';
const DATE_TIME_FORMAT = `${DATE_FORMAT} ${TIME_FORMAT}`;

const textValidator = function (this: GridSettings, text: string | undefined, cb: (isValid: boolean) => void) {
  cb(this.allowEmpty || (text ? text.length > 0 : false));
};

const createTextColumnWithGenericValidation = function (settings: ExtendedGridSettings): ExtendedGridSettings {
  const validator = function (value: string, cb: (valid: boolean) => void): void | RegExp {
    cb(settings.allowEmpty || value?.length > 0);
  };

  return {
    type: 'text',
    validator,
    ...settings,
  };
};

const positiveIntegerValidator = function (value: string, cb: (isValid: boolean) => void) {
  const number = Number(value);

  if (number > -1 && Math.floor(number) === number) {
    cb(true);
  } else {
    cb(false);
  }
};

const STATIC_COL_HEADERS = ['Arrival date (MM/DD/YYYY)', 'Arrival time', 'Full name', 'Visitor email address'];

// We use functions instead of static lists here because the columns and date validator both rely on
// handsontable, which is passed in as a component property and therefore not available until
// the component is actually rendered.
const createDateValidator = (handsontable: HandsontableConfig) => {
  return function (this: ExtendedGridSettings, dateStr: string, cb: (isValid: boolean) => void) {
    let date: Date;
    if (ISO8601_DATE_FORMAT.test(dateStr)) {
      // The value we receive from the datepicker is an ISO8601-formatted string for the LOCATION timezone,
      // not for the current user's time zone. So, we want to convert that string into an actual Date.
      date = parseISO(dateStr);
    } else {
      // For values entered directly by a user, use legacy behavior (i.e., let moment handle it)
      date = moment(dateStr, DATE_FORMAT).toDate();
    }

    handsontable.validators.DateValidator.call(this, date, (isValid) => {
      if (!isValid) {
        cb(false);
        return;
      }

      // Make sure the date the user picked is today or a future date.
      const startOfToday = startOfDay(new Date());
      const valid = isSameDay(date, startOfToday) || isAfter(date, startOfToday);
      cb(valid);
    });
  };
};

const createStaticColumns = (handsontable: HandsontableConfig, isEmailRequired = false): ExtendedGridSettings[] => {
  return [
    {
      allowEmpty: false,
      correctFormat: true,
      csvHeader: 'expected_arrival_time',
      data: 'arrivalDate',
      dateFormat: 'YYYY-MM-DDTHH:mm:ssZ', // momentjs uses this format string for the selected date in the datepicker
      datePickerConfig: {
        minDate: new Date(),
      },
      type: 'date',
      validator: createDateValidator(handsontable),
    },
    {
      allowEmpty: false,
      correctFormat: true,
      csvHeader: 'expected_arrival_time',
      data: 'arrivalTime',
      timeFormat: TIME_FORMAT,
      type: 'time',
    },
    {
      allowEmpty: false,
      csvHeader: 'invitee_name',
      data: 'fullName',
      type: 'text',
      validator: textValidator,
    },
    {
      allowEmpty: !isEmailRequired,
      csvHeader: 'invitee_email',
      data: 'email',
      type: 'text',
      validator(val: string, cb) {
        if (isBlank(val)) {
          cb(isEmailRequired ? false : true);
        } else {
          cb(/@/.test(val));
        }
      },
    },
  ];
};

interface SpreadsheetPreregComponentSignature {
  Args: {
    changeset: DetailedChangeset<InviteModel>;
    flows: FlowModel[];
    isEditInvite?: boolean;
    locations: LocationModel[];
    locationIsConnectedToProperty?: boolean;
    onLocationSelectedTask?: Task<unknown, [LocationModel]>;
    selectedFlow: FlowModel;
    handsontable: HandsontableConfig;
    onFlowChanged?: (flowName: string, changeset: DetailedChangeset<InviteModel>) => void;
    vrSubscription: SubscriptionModel;
  };
}

type SecondaryLocationOption = { location: LocationModel; eligible: boolean; disabled: boolean };
type FlowOption = { flow: FlowModel; eligible: boolean; disabled: boolean };

export default class SpreadsheetPreregComponent extends Component<SpreadsheetPreregComponentSignature> {
  @service declare ajaxFetch: AjaxFetchService;
  @service declare currentLocation: CurrentLocationService;
  @service declare flashMessages: FlashMessagesService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare locationFeatureFlags: LocationFeatureFlagsService;
  @service declare metrics: MetricsService;
  @service declare router: RouterService;
  @service declare session: SessionService;
  @service declare state: StateService;
  @service declare store: StoreService;

  // Tracked source of truth for primary location id so `flowOptions` /
  // `flowOptionIndex` re-fire on in-form switches. `invite.belongsTo` is
  // tag-blind and `changeset.location.id` trips PromiseBelongsTo races.
  @tracked private _primaryLocationId: string | null = null;

  @tracked bulkInviteId: string | null = null; // Id used to track batches across failed uploads
  @tracked errorRows?: number[] | ValidationErrorForRow[];
  @tracked validationErrors?: ValidationErrorForRow[];
  @tracked flowNameToChangeTo?: FlowModel;
  @tracked groupName = '';
  @tracked hasEmail = false;
  @tracked hotInstance!: Handsontable;
  @tracked includeGroupNameInEmail = true;
  @tracked notifyVisitor = false;
  @tracked rowLength = 0;
  @tracked showConfirmVisitorTypeChangeModal = false;
  @tracked showPreregistrationEmailConfirmation = false;

  // Lazily populated by loadScopedSubsForCandidatesTask. Reassigned, not mutated.
  @tracked scopedSubsByLocation: ReadonlyMap<string, ScopedSubLookup> = new Map();

  @localCopy('args.flows', []) flows!: FlowModel[];

  constructor(owner: unknown, args: SpreadsheetPreregComponentSignature['Args']) {
    super(owner, args);
    // Prefer the changeset's pending location over the underlying record's id
    // so initial eligibility gates against what the user will actually save.
    // eslint-disable-next-line ember/use-ember-get-and-set
    const pendingId = this.args.changeset?.location?.get?.('id');
    const invite = this.args.changeset?.data as InviteModel | undefined;
    const inviteId = invite?.belongsTo('location').id();
    this._primaryLocationId = pendingId ?? inviteId ?? null;
  }

  // Fans out one scoped-subscription fetch per candidate location — the only
  // non-admin path that resolves the MPB location→subscription mapping.
  // restartableTask cancels stale fan-outs when the flow or candidate set
  // changes; ids already in the map are skipped (dedup).
  loadScopedSubsForCandidatesTask = restartableTask(async (locationIds: readonly string[]) => {
    const companyId = this.state.currentCompany?.id;
    if (!companyId) return;

    const next = new Map(this.scopedSubsByLocation);
    const toFetch = locationIds.filter((id) => id && !next.has(id));
    if (toFetch.length === 0) return;

    // eslint-disable-next-line ember/use-ember-get-and-set
    for (const id of toFetch) next.set(id, { state: 'loading' });
    if (this.isDestroying || this.isDestroyed) return;
    this.scopedSubsByLocation = next;

    let results: readonly (readonly [string, ScopedSubLookup])[];
    try {
      results = await Promise.all(
        toFetch.map(async (locationId): Promise<readonly [string, ScopedSubLookup]> => {
          try {
            const records = await this.store.query('scoped-subscription', {
              filter: { scope: 'mixed-plan', company_id: companyId, location_id: locationId, app: 'visitors' },
            });
            const [sub] = records.slice();
            return [
              locationId,
              {
                state: 'loaded',
                plan: sub?.plan ?? null,
                canAccessGlobalSignInFlows: sub?.canAccessGlobalSignInFlows ?? false,
              },
            ];
          } catch {
            // Fail open on any read failure here. 401 is already handled
            // centrally by `billing-v1` adapter's `handleResponse`, which
            // calls `session.invalidate()` before the error reaches us.
            // 403/5xx/network are treated the same as a missing scoped sub —
            // helpers fall back to fail-open; the backend save path remains
            // the final gate, and locking the picker to all-disabled would
            // mask transient billing outages.
            return [locationId, { state: 'error' }];
          }
        }),
      );
    } catch (e) {
      // restartableTask cancellation throws TaskCancelation at the await. Roll
      // back the 'loading' sentinels we wrote so a subsequent perform() doesn't
      // dedup-skip these ids (they'd otherwise be silently stranded as loading
      // for the component lifetime).
      if (didCancel(e) && !this.isDestroying && !this.isDestroyed) {
        const rolled = new Map(this.scopedSubsByLocation);
        for (const id of toFetch) {
          // eslint-disable-next-line ember/use-ember-get-and-set
          if (rolled.get(id)?.state === 'loading') rolled.delete(id);
        }
        this.scopedSubsByLocation = rolled;
      }
      throw e;
    }

    if (this.isDestroying || this.isDestroyed) return;
    const settled = new Map(this.scopedSubsByLocation);
    // eslint-disable-next-line ember/use-ember-get-and-set
    for (const [id, lookup] of results) settled.set(id, lookup);
    this.scopedSubsByLocation = settled;
  });

  // Triggered by template modifier on first render and whenever the
  // selected flow or primary location changes. Fetches the primary (for
  // flow-picker gating) and the global flow's locations (for secondary-
  // picker gating). The task dedupes against ids already in the map.
  @action
  ensureScopedSubsForCandidates(): void {
    if (!this.featureFlags.isEnabled(MPB_FEATURE_FLAG)) return;
    void this.fetchScopedSubsForSelectedFlow();
  }

  // Combines primary + global-flow siblings into a single fan-out so the
  // restartableTask doesn't cancel its own first perform and strand the
  // primary as fail-open. Awaits the AsyncBelongsTo before reading hasMany.
  private async fetchScopedSubsForSelectedFlow(): Promise<void> {
    const primaryLocationId =
      // eslint-disable-next-line ember/use-ember-get-and-set
      this._primaryLocationId ?? this.args.changeset.location.get('id') ?? null;
    const ids: string[] = [];
    if (primaryLocationId) ids.push(primaryLocationId);

    const flow = this.selectedFlow as FlowModel | undefined;
    if (flow?.globalFlow) {
      try {
        const globalFlow = (await flow.globalFlow) as GlobalFlowModel | null;
        if (this.isDestroying || this.isDestroyed) return;
        if (globalFlow) {
          // eslint-disable-next-line ember/no-get
          const locationsProxy = (await get(globalFlow, 'locations')) as
            | AsyncHasMany<SkinnyLocationModel>
            | SkinnyLocationModel[]
            | undefined;
          if (this.isDestroying || this.isDestroyed) return;
          if (locationsProxy) {
            const skinnyLocations = Array.isArray(locationsProxy) ? locationsProxy : locationsProxy.slice();
            for (const loc of skinnyLocations) {
              if (loc.id !== primaryLocationId) ids.push(loc.id);
            }
          }
        }
      } catch {
        // Relationship-fetch failure: fall through with primary-only ids.
      }
    }

    if (ids.length === 0) return;
    void this.loadScopedSubsForCandidatesTask.perform(ids);
  }

  /* eslint-disable ember/no-get */
  @computed(
    'selectedFlow.{id,signInFieldPage.signInFields.@each.name,signInFieldPage.signInFields.isSettled}',
    'state.currentLocation.id',
    'args.changeset.location.id',
  )
  get signInFields(): SignInFieldModel[] {
    if (!this.selectedFlow) return [];

    const selectedFlow = <FlowModel>this.selectedFlow;
    let flowWithFields: FlowModel = selectedFlow;
    let signInFields: SignInFieldModel[] = [];

    try {
      // If the selected flow is a global flow, it shares a single set of sign-in fields with its
      // global child siblings. Scan the siblings for the sign-in fields to render.
      if (selectedFlow.isGlobalChild) {
        const globalChildSiblings: FlowModel[] = selectedFlow.globalChildSiblings;

        for (const child of globalChildSiblings) {
          // Add null checks for child flow and its relationships
          if (!child || child.isDestroyed || child.isDestroying) continue;

          try {
            const childFields: SignInFieldModel[] =
              (<AsyncHasMany<SignInFieldModel> | undefined>(
                get(child, 'signInFieldPage.signInFields')
              ))?.content?.slice() ?? [];

            if (childFields.length) {
              flowWithFields = child;
              signInFields = childFields;
              break;
            }
          } catch (childError) {
            this.metrics.trackEvent('GlobalChild Flow Warning', {
              event: 'sibling_field_access_error',
              childFlowId: child.id,
              error: childError instanceof Error ? childError.message : 'Unknown error',
            });
            continue;
          }
        }
      } else {
        signInFields =
          (<AsyncHasMany<SignInFieldModel> | undefined>(
            get(selectedFlow, 'signInFieldPage.signInFields')
          ))?.content?.slice() ?? [];
      }

      if (signInFields?.length) {
        const flowId = get(this.selectedFlow, 'id') ?? get(this.selectedFlow, 'content.id') ?? 'unknown';
        const pageId = <string>(get(flowWithFields, 'signInFieldPage.id') ?? 'unknown');
        this.metrics.trackEvent('Sign-in Fields Loaded', {
          flowId,
          pageId,
          totalFields: signInFields.length,
          fieldNames: signInFields.map((field) => field.name),
        });
      }
    } catch (error) {
      this.metrics.trackEvent('Sign-in Fields Error', {
        event: 'loading_error',
        flowId: selectedFlow?.id || 'unknown',
        flowType: selectedFlow?.type || 'unknown',
        error: error instanceof Error ? error.message : 'Unknown error',
      });
    }

    return signInFields;
  }
  /* eslint-enable ember/no-get */

  get location(): AsyncBelongsTo<LocationModel> {
    return this.args.changeset?.location;
  }

  get secondaryLocations(): LocationModel[] | AsyncHasMany<LocationModel> {
    return this.args.changeset.childInviteLocations || [];
  }

  get shouldShowSecondaryLocations(): boolean {
    return (
      this.featureFlags.isEnabled('multi-location-group-invites') &&
      !!this.state.features?.canAccessMultiLocationInvites &&
      this.args.locations.length > 1
    );
  }

  get locationSelectorId(): string {
    return `${guidFor(this)}-location-select`;
  }

  get flowSelectorId(): string {
    return `${guidFor(this)}-flow-select`;
  }

  get secondaryLocationSelectorId(): string {
    return `${guidFor(this)}-secondary-locations-select`;
  }

  get showMultiLocationToolTip(): boolean {
    return !(<FlowModel>this.selectedFlow)?.isGlobalChild && !this.args.isEditInvite;
  }

  @computed(
    'flows.@each.name',
    'purposeOfVisit.value',
    'args.changeset.flowName',
    'state.currentLocation.employeeScreeningFlow.id',
  )
  get selectedFlow(): AbstractFlowModel | null | undefined {
    // the flow rel is always correct and might not match the flowName or purpose of visit
    // eslint-disable-next-line ember/use-ember-get-and-set
    const flowId = this.args.changeset.get('flow.id');
    if (!flowId) {
      return null;
    }

    if (this.purposeOfVisit.value === PURPOSE_OF_VISIT.EMPLOYEE_REGISTRATION) {
      return this.state.currentLocation.employeeScreeningFlow;
    }

    return this.flows.find((flow) => flow.id === flowId);
  }

  // @cached so PowerSelect sees a stable Promise reference and doesn't
  // re-await on every render.
  @cached
  get optionsForSecondaryLocations(): Promise<SecondaryLocationOption[]> | SecondaryLocationOption[] {
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    if (!(<FlowModel>this.selectedFlow)?.globalFlow) return [];
    const primaryLocationId = this._primaryLocationId ?? undefined;
    const locationsRel = <
      AsyncHasMany<SkinnyLocationModel> | undefined // eslint-disable-next-line ember/no-get
    >get((<FlowModel>this.selectedFlow).globalFlow, 'locations');
    if (!locationsRel) return [];

    const isMpbEnabled = this.featureFlags.isEnabled(MPB_FEATURE_FLAG);
    // Read synchronously so the getter consumes the tracked tag — without this
    // the .then() reads it after the getter returns and the getter never re-fires
    // when the fan-out settles a 'loading' → 'loaded' entry.
    const scopedSubs = this.scopedSubsByLocation;

    return locationsRel.then((skinnyLocations) => {
      return skinnyLocations
        .filter((loc) => loc.id !== primaryLocationId)
        .map((skinnyLocation) => this.store.peekRecord('location', skinnyLocation.id))
        .filter((location): location is LocationModel => location !== null)
        .map((location) => {
          const eligible = isLocationEligibleFromScopedMap(location.id, isMpbEnabled, scopedSubs);
          return { location, eligible, disabled: !eligible };
        });
    });
  }

  // @cached so chip wrappers have stable identity across renders —
  // PowerSelectMultiple's `===` is-selected check needs this, and the
  // spreadsheet variant doesn't consult `optionIndex`, so every wrapper
  // is independently constructed.
  @cached
  get selectedSecondaryLocationOptions(): SecondaryLocationOption[] {
    const selected = (this.args.changeset.childInviteLocations ?? []) as LocationModel[];
    const isMpbEnabled = this.featureFlags.isEnabled(MPB_FEATURE_FLAG);
    // Compute independently — sharing a cache with the async options getter
    // would race when the locations relationship resolves synchronously
    // (cached) inside the same render computation.
    return selected.map((location) => {
      const eligible = isLocationEligibleFromScopedMap(location.id, isMpbEnabled, this.scopedSubsByLocation);
      return { location, eligible, disabled: !eligible };
    });
  }

  @computed('signInFields.@each.isTopLevel')
  get customFields(): NativeArray<SignInFieldModel> {
    return A(
      this.signInFields?.filter(
        // Filter out built-in fields and conditional fields (those triggered by other fields).
        // Conditional fields have isTopLevel === false; top-level fields have isTopLevel === true.
        // We check !== false rather than === true to handle edge cases where isTopLevel is undefined.
        (field) => !['name', 'host', 'email'].includes(field.identifier) && field.isTopLevel !== false,
      ),
    ).sortBy('position');
  }

  @computed('signInFields.[]')
  get hostField(): SignInFieldModel | undefined {
    return A(this.signInFields).findBy('identifier', 'host');
  }

  @computed('canSetPropertyNotes', 'customFields.[]', 'hostField', 'selectedFlow.additionalGuests')
  get colHeaders(): string[] {
    const colHeaders = STATIC_COL_HEADERS.slice();

    const hostField = this.hostField;
    if (hostField) {
      const { name, localized, required } = hostField;
      if (required) {
        colHeaders.push(localized || name);
      } else {
        colHeaders.push(`${localized || name} (optional)`);
      }
    }

    if ((<FlowModel>this.selectedFlow).additionalGuests) {
      colHeaders.push('Additional visitors');
    }

    this.customFields.forEach((field) => {
      const { name, localized, kind } = field;
      if (field.isDate) {
        const fmt = field.dateFormat ?? DateFieldFormat.MM_DD_YYYY;
        colHeaders.push(`${localized || name} (${fmt})`);
      } else if (kind === SignInFieldKind.PHONE) {
        colHeaders.push('Visitor phone number');
      } else {
        colHeaders.push(localized || name);
      }
    });

    colHeaders.push('Private notes');
    if (this.canSetPropertyNotes) {
      colHeaders.push('Shared notes');
    }
    return colHeaders.map((h) => escapeHTML(h));
  }

  @computed(
    'args.handsontable',
    'canSetPropertyNotes',
    'customFields.[]',
    'hostField',
    'isEmailRequired',
    'searchEmployeesTask',
    'selectedFlow.additionalGuests',
    'signInFields.@each.required',
  )
  get columns(): ExtendedGridSettings[] {
    const columns = createStaticColumns(this.args.handsontable, this.isEmailRequired);

    const hostField = this.hostField;
    if (hostField) {
      columns.push({
        allowEmpty: !hostField.required,
        csvHeader: 'host_name',
        data: 'host',
        type: 'autocomplete',
        // @ts-ignore
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        source: (term: string, cb: () => void) => this.searchEmployeesTask.perform(term, cb),
        validator: textValidator,
      });
    }

    if ((<FlowModel>this.selectedFlow).additionalGuests) {
      columns.push({
        allowEmpty: true,
        csvHeader: 'additional_guests',
        data: 'additionalGuests',
        type: 'text',
        validator: positiveIntegerValidator,
      });
    }

    this.customFields.forEach((field) => {
      const { name, isRequestedFromEmployee, kind } = field;
      const fieldOptions = field.options;
      let options: string[] = [];
      if (typeof fieldOptions === 'object' && fieldOptions.length && fieldOptions[0] instanceof DropdownOption) {
        options = fieldOptions.mapBy('value');
      } else {
        options = <string[]>(<unknown>fieldOptions);
      }

      if (field.isDate) {
        const dateFormat = field.dateFormat ?? DateFieldFormat.MM_DD_YYYY;
        const allowEmpty = !isRequestedFromEmployee;
        columns.push({
          allowEmpty,
          correctFormat: true,
          csvHeader: name,
          data: escapeHTML(name),
          dateFormat,
          type: 'date',
          validator: createDateFieldValidator(dateFormat, allowEmpty),
        });
      } else {
        columns.push(
          createTextColumnWithGenericValidation({
            allowEmpty: !isRequestedFromEmployee,
            csvHeader: name,
            data: escapeHTML(name),
            source: kind === SignInFieldKind.SINGLE_SELECTION ? options : [],
            type: kind === SignInFieldKind.SINGLE_SELECTION ? 'dropdown' : 'text',
          }),
        );
      }
    });

    columns.push({
      allowEmpty: true,
      csvHeader: 'private_notes',
      data: 'privateNotes',
      type: 'text',
    });

    if (this.canSetPropertyNotes) {
      columns.push({
        allowEmpty: true,
        csvHeader: 'property_notes',
        data: 'propertyNotes',
        type: 'text',
      });
    }

    return columns;
  }

  @computed('rowLength', 'submit.isRunning')
  get isSubmitDisabled(): boolean {
    return this.rowLength === 0 || this.submit.isRunning;
  }

  @computed('rowLength', 'hasEmail', 'submit.isRunning')
  get isSendPreRegEmailDisabled(): boolean {
    const hasNoRows = this.rowLength === 0;
    const isRunning = this.submit.isRunning;
    const hasNoEmail = !this.hasEmail;
    return hasNoRows || isRunning || hasNoEmail;
  }

  // eslint-disable-next-line ember/require-computed-property-dependencies
  @computed('args.changeset.{userData.@each.field,flowName}')
  get purposeOfVisit(): UserDatum | Partial<UserDatum> {
    const flow = this.args.changeset.flow;
    const userData = this.args.changeset.userData || A();
    const flowName = (<FlowModel>(<unknown>flow))?.name || this.args.changeset.flowName;

    return userData.findBy('field', 'Purpose of visit') || { field: 'Purpose of visit', value: flowName };
  }

  get canSetPropertyNotes(): boolean | undefined {
    return this.args.locationIsConnectedToProperty;
  }

  get emailSignInField(): SignInFieldModel | undefined {
    return this.signInFields?.find((field) => field.isEmail);
  }

  @cached
  private get primaryLocationForEligibility(): LocationModel | null {
    return this._primaryLocationId ? this.store.peekRecord('location', this._primaryLocationId) : null;
  }

  @cached
  get flowOptions(): FlowOption[] {
    const isMpbEnabled = this.featureFlags.isEnabled(MPB_FEATURE_FLAG);
    const primaryLocation = this.primaryLocationForEligibility;
    return A(this.flows)
      .rejectBy('isProtect')
      .sortBy('position')
      .map((flow) => {
        const eligible = isFlowEligibleForLocation(
          flow,
          primaryLocation,
          isMpbEnabled,
          this.state,
          this.scopedSubsByLocation,
          this._primaryLocationId,
        );
        return { flow, eligible, disabled: !eligible };
      });
  }

  @cached
  private get flowOptionIndex(): Readonly<Record<string, FlowOption>> {
    const index: Record<string, FlowOption> = {};
    for (const option of this.flowOptions) {
      index[option.flow.id] = option;
    }
    return index;
  }

  @cached
  get selectedFlowOption(): FlowOption | undefined {
    const flow = this.selectedFlow as FlowModel | null | undefined;
    if (!flow || !('id' in flow) || !flow.id) return undefined;

    // Match by id, not reference — Ember Data may re-instantiate a record
    // and break `===` while leaving the id stable.
    const cached = this.flowOptionIndex[flow.id];
    if (cached && cached.flow.id === flow.id) return cached;

    // Fallback for a saved flow no longer in the picker. Compute eligibility
    // against the current primary so the chip's disabled state matches.
    const isMpbEnabled = this.featureFlags.isEnabled(MPB_FEATURE_FLAG);
    const eligible = isFlowEligibleForLocation(
      flow,
      this.primaryLocationForEligibility,
      isMpbEnabled,
      this.state,
      this.scopedSubsByLocation,
      this._primaryLocationId,
    );
    return { flow, eligible, disabled: !eligible };
  }

  get isEmailRequired(): boolean {
    if (
      !(
        this.featureFlags.isEnabled('visitors-required-email-field') ||
        this.locationFeatureFlags.isEnabled('visitors-required-email-field-by-location')
      )
    )
      return false;

    return !!this.emailSignInField?.required;
  }

  // prettier-ignore
  @(employeesSearcherTask({
    filter: {
      deleted: false,
      excludeHidden: true,
    },
    prefix: true,
  }).restartable())
  _searchEmployeesTask!: EmployeeSearcherTask;

  /**
   * Check if OAuth token expires within the specified seconds
   */
  private isTokenExpiringWithin(seconds: number): boolean {
    const expiresAt = this.session.data?.authenticated?.['expires_at'];
    if (!expiresAt) return false;

    const now = Date.now();
    const expiresAtMs = typeof expiresAt === 'number' ? expiresAt : new Date(expiresAt).getTime();
    const timeUntilExpiry = expiresAtMs - now;

    return timeUntilExpiry < seconds * 1000;
  }

  /**
   * Attempt to refresh the OAuth token by calling the authenticator's refresh method
   * Returns true if refresh was successful, false otherwise
   */
  private async refreshToken(): Promise<boolean> {
    try {
      const refreshToken = this.session.data?.authenticated?.['refresh_token'];
      const expiresIn = this.session.data?.authenticated?.['expires_in'];
      const oldAccessToken = this.session.data?.authenticated?.['access_token'];

      if (!refreshToken) {
        return false;
      }

      // Get the authenticator instance from the session
      // We need to access private ember-simple-auth APIs here
      // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
      const sessionInternal = this.session as any;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      const authenticatorName = sessionInternal.session?.authenticator;
      if (!authenticatorName) {
        return false;
      }

      // Access private _lookupAuthenticator method
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      const authenticator = sessionInternal.session._lookupAuthenticator(authenticatorName);

      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (!authenticator || typeof authenticator._refreshAccessToken !== 'function') {
        return false;
      }

      // Call the authenticator's _refreshAccessToken method
      // This returns a promise that resolves with the new session data
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      await authenticator._refreshAccessToken(expiresIn, refreshToken);

      // Verify token was actually refreshed by checking for a new access_token
      const newAccessToken = this.session.data?.authenticated?.['access_token'];
      return !!newAccessToken && newAccessToken !== oldAccessToken;
    } catch (error) {
      this.metrics.trackEvent('Token Refresh Failed', {
        error: error instanceof Error ? error.message : 'Unknown error',
      });
      return false;
    }
  }

  submit = dropTask(async () => {
    const { flashMessages, hotInstance, validationErrors = [] } = this;

    flashMessages.hideAll();

    // Block submit when the selected flow became ineligible (e.g., primary
    // switched mid-form to a location lacking global_sign_in_flows).
    if (this.selectedFlowOption && !this.selectedFlowOption.eligible) {
      flashMessages.showFlash(
        'error',
        "This visitor type isn't available on this location's plan. Pick another type or upgrade the location.",
      );
      return;
    }

    if (validationErrors?.length) {
      this.errorRows = [...validationErrors];
      return;
    }

    const headers = _uniq(this.columns.map((c) => c.csvHeader));
    headers.unshift('Purpose of visit');

    const flowName = this.selectedFlow?.name;

    const dateFieldColumns = this.columns
      .map((col, i) =>
        col.type === 'date' && col.csvHeader !== 'expected_arrival_time'
          ? { index: i, format: col.dateFormat as DateFieldFormat }
          : null,
      )
      .filter((item): item is { index: number; format: DateFieldFormat } => item !== null);

    const data = A(
      hotInstance.getData().map((row: [string, string, string, ...string[]], i) => {
        if (hotInstance.isEmptyRow(i)) {
          return;
        }

        const additionalGuestsIndex = this.colHeaders.indexOf('Additional visitors');
        if (additionalGuestsIndex > -1 && isBlank(row[additionalGuestsIndex])) {
          (<number>(<unknown>row[additionalGuestsIndex])) = 0;
        }

        dateFieldColumns.forEach(({ index, format }) => {
          const val = row[index];
          if (val && typeof val === 'string') {
            const iso = convertToISO(val, format);
            if (iso) row[index] = iso;
          }
        });

        row.unshift(flowName!);

        const [flow, date, time, ...rest] = row;

        const dateAndTime = time ? `${date} ${time}` : date;
        let iso8601 = '';
        if (moment(dateAndTime, DATE_TIME_FORMAT).isValid()) iso8601 = dateAndTime;

        return [flow, iso8601, ...rest];
      }),
    ).compact();
    const csv = Papa.unparse([headers, ...data]);

    const job_id = uuid();
    const invite_count = data.length;
    const visitor_type = flowName;
    const notify_invitees = this.notifyVisitor;
    this.metrics.trackEvent('Bulk Invite Creation Requested', { invite_count, visitor_type, notify_invitees, job_id });
    const childInviteLocations = this.args.changeset.childInviteLocations?.map((location) => {
      return { id: location.id.toString(), type: 'locations' };
    });

    // Pre-flight token check: refresh if token expires within 60 seconds
    if (this.isTokenExpiringWithin(60)) {
      this.metrics.trackEvent('Token Expiring Soon', {
        context: 'bulk_invite_preflight',
        job_id,
      });
      const preflightRefreshed = await this.refreshToken();

      // If pre-flight refresh failed and token is already expired, bail early
      if (!preflightRefreshed && this.isTokenExpiringWithin(0)) {
        const msg = `Failed to add ${pluralize(data.length, 'invite')}!`;
        this.flashMessages.showAndHideFlash(
          'error',
          msg,
          'Your session expired. Please reload the page and try again.',
        );
        this.metrics.trackEvent('Viewed Flash Message', {
          type: 'error',
          message_title: msg,
          message_codes: ['error_group_invites', 'token_refresh_failed'],
          job_id,
        });
        return;
      }
    }

    const url = urlBuilder.v3.invites.bulkUrl();
    let result: BulkInvitePayload;
    let retryAttempted = false;

    const makeRequest = async (): Promise<BulkInvitePayload> => {
      return this.ajaxFetch.request(url, {
        type: 'POST',
        headers: { accept: 'application/vnd.api+json' },
        contentType: 'application/vnd.api+json',
        data: JSON.stringify({
          data: {
            type: 'invites',
            attributes: {
              csv,
              'job-id': job_id,
              'notify-visitor': this.notifyVisitor,
              'group-name': this.groupName,
              'include-group-name-in-email': isPresent(this.groupName) && this.includeGroupNameInEmail,
              'bulk-invite-id': this.bulkInviteId,
            },
            relationships: {
              location: {
                data: {
                  type: 'locations',
                  id: (await this.location)?.id.toString(),
                },
              },
              'child-invite-locations': {
                data: childInviteLocations,
              },
            },
          },
        }),
      });
    };

    try {
      result = await makeRequest();
    } catch (error) {
      // Retry on 401: attempt token refresh once and retry the request
      if (error instanceof UnauthorizedError && !retryAttempted) {
        retryAttempted = true;
        this.metrics.trackEvent('Token Expired During Request', {
          context: 'bulk_invite_retry',
          job_id,
        });

        const refreshed = await this.refreshToken();
        if (refreshed) {
          try {
            result = await makeRequest();
          } catch (retryError) {
            const err = <Error>retryError;
            const msg = `Failed to add ${pluralize(data.length, 'invite')}!`;
            const subtitle =
              retryError instanceof UnauthorizedError
                ? 'Your session expired. Please reload the page and try again.'
                : err && err.message
                  ? err.message
                  : '';

            this.flashMessages.showAndHideFlash('error', msg, subtitle);
            this.metrics.trackEvent('Viewed Flash Message', {
              type: 'error',
              message_title: msg,
              message_codes: ['error_group_invites', 'token_refresh_retry_failed'],
              job_id,
            });
            return;
          }
        } else {
          const msg = `Failed to add ${pluralize(data.length, 'invite')}!`;
          this.flashMessages.showAndHideFlash(
            'error',
            msg,
            'Your session expired. Please reload the page and try again.',
          );
          this.metrics.trackEvent('Viewed Flash Message', {
            type: 'error',
            message_title: msg,
            message_codes: ['error_group_invites', 'token_refresh_failed'],
            job_id,
          });
          return;
        }
      } else {
        const err = <Error>error;
        const msg = `Failed to add ${pluralize(data.length, 'invite')}!`;
        const subtitle = err && err.message ? err.message : '';

        this.flashMessages.showAndHideFlash('error', msg, subtitle);
        this.metrics.trackEvent('Viewed Flash Message', {
          type: 'error',
          message_title: msg,
          message_codes: ['error_group_invites'],
          job_id,
        });
        return;
      }
    }

    // SQ-7414: the backend defers large imports (202 + queued/running). Poll the
    // bulk invite until it finishes, then fall through to the same
    // success-rows/error-rows handling the synchronous 201 path uses.
    const importStatus = result.data.attributes['status'];
    if (importStatus === 'queued' || importStatus === 'running') {
      try {
        result = await this.pollBulkInvite(result.data.attributes['bulk-invite-id']!);
      } catch (error) {
        const err = <Error>error;
        const msg = `Failed to add ${pluralize(data.length, 'invite')}!`;
        this.flashMessages.showAndHideFlash('error', msg, err && err.message ? err.message : '');
        this.metrics.trackEvent('Viewed Flash Message', {
          type: 'error',
          message_title: msg,
          message_codes: ['error_group_invites', 'async_poll_failed'],
          job_id,
        });
        return;
      }
    }

    // SQ-7414: a deferred import that finished in an error state carries no
    // per-row detail — surface a clear failure and keep the user on the
    // spreadsheet instead of falling through to the success path.
    if (result.data.attributes['status'] === 'error') {
      const msg = `Failed to add ${pluralize(data.length, 'invite')}!`;
      this.flashMessages.showAndHideFlash('error', msg, 'The import could not be completed. Please try again.');
      this.metrics.trackEvent('Viewed Flash Message', {
        type: 'error',
        message_title: msg,
        message_codes: ['error_group_invites', 'async_import_failed'],
        job_id,
      });
      return;
    }

    const successRows = result.data.attributes['success-rows'];
    const errorRows = result.data.attributes['error-rows'];
    this.errorRows = errorRows;
    this.bulkInviteId = result.data.attributes['bulk-invite-id']!;

    if (successRows && successRows.length > 0) {
      const msg = `${pluralize(successRows.length, 'invite')} added!`;
      this.flashMessages.showAndHideFlash('success', msg);
      this.metrics.trackEvent('Viewed Flash Message', {
        type: 'success',
        message_title: msg,
        message_codes: [],
        job_id,
      });
    }

    successRows?.reverse().forEach((row) => hotInstance.alter('remove_row', row));

    if (errorRows && errorRows.length === 0) {
      this.notifyVisitor = false;
      this.groupName = '';
      this.includeGroupNameInEmail = true;
      this.bulkInviteId = null;

      this.args.changeset.rollback();
      const firstInvite = data[0];
      let date = firstInvite?.[1]?.split(' ')[0];
      date = moment(date).format('YYYY-MM-DD');
      this.router.transitionTo('visitors.invites.index', { queryParams: { date } });
    }
  });

  // SQ-7414: poll the deferred bulk-invite import until it completes or errors,
  // returning the final payload so submit() can reuse its existing row handling.
  // Mirrors the location-billing subscription-batch polling pattern.
  async pollBulkInvite(bulkInviteId: string): Promise<BulkInvitePayload> {
    const url = urlBuilder.v3.invites.bulkStatusUrl(bulkInviteId);

    for (let attempt = 0; attempt < BULK_INVITE_MAX_POLLS; attempt++) {
      await timeout(zft(BULK_INVITE_POLL_INTERVAL_MS));

      const result: BulkInvitePayload = await this.ajaxFetch.request(url, {
        type: 'GET',
        headers: { accept: 'application/vnd.api+json' },
      });

      const status = result.data.attributes['status'];
      if (status === 'complete' || status === 'error') {
        return result;
      }
    }

    throw new Error('Bulk invite import did not finish in time. The invites may still be processing.');
  }

  onRowUpdate = task({ restartable: true }, async (hotInstance: Handsontable) => {
    await timeout(zft(1));

    const data = hotInstance.getData();
    const nonEmptyRowCount = data.reduce<number>((acc, _, i) => (!hotInstance.isEmptyRow(i) ? acc + 1 : acc), 0);

    this.rowLength = nonEmptyRowCount;

    const { changeset } = this.args;

    if (nonEmptyRowCount > 0) {
      set(changeset, '_dirtyMarker', true);
      if (this.pluckEmails(hotInstance).length > 0) {
        this.hasEmail = true;
      } else {
        this.hasEmail = false;
        this.notifyVisitor = false;
      }
    } else {
      // Preserve the user-selected visitor type across rollback so that
      // interacting with empty rows doesn't revert it to the default flow.
      // Persist on the underlying invite (rather than re-setting on the
      // changeset after rollback) so the changeset stays clean — otherwise
      // the form would be flagged as dirty for unrelated nav-confirm checks.
      // eslint-disable-next-line ember/use-ember-get-and-set
      const currentFlowId = <string | null | undefined>changeset.get('flow.id');
      const currentFlowName = <string | undefined>changeset.flowName;
      const currentFlow = currentFlowId ? this.flows.find((f) => f.id === currentFlowId) : undefined;
      if (currentFlow && currentFlowName) {
        const invite = changeset.data;
        // @ts-expect-error - belongsTo accepts a model at runtime
        invite.flow = currentFlow;
        invite.flowName = currentFlowName;
      }
      changeset.rollback();
      this.hasEmail = false;
      this.notifyVisitor = false;
    }

    await this.validationTask.perform(hotInstance);
  });

  validationTask = task({ restartable: true }, async (hotInstance: Handsontable) => {
    const data = hotInstance.getData();
    const validationErrors: ValidationErrorForRow[] = [];
    const columns = this.columns;

    await Promise.all(
      data
        .map((_: unknown[], i: number) => {
          if (hotInstance.isEmptyRow(i)) return;

          return new Promise((resolve) => {
            hotInstance.validateRows([i], (valid) => {
              if (!valid) {
                const error = this._getRowValidationError(hotInstance, i, columns);
                validationErrors.push({
                  invalidRow: i + 1,
                  error,
                });
              }
              resolve(valid);
            });
          });
        })
        .filter((promise) => promise !== undefined),
    );

    this.validationErrors = validationErrors;
  });

  _getRowValidationError(hotInstance: Handsontable, rowIndex: number, columns: ExtendedGridSettings[]): string {
    for (let colIndex = 0; colIndex < columns.length; colIndex++) {
      const col = columns[colIndex];
      if (col?.type !== 'date' || col.data === 'arrivalTime' || col.data === 'arrivalDate') continue;

      const cellValue = hotInstance.getDataAtCell(rowIndex, colIndex) as string | null;
      if (cellValue == null || String(cellValue).trim().length === 0) continue;

      const dateFormat = col.dateFormat as DateFieldFormat | undefined;
      if (dateFormat && !validateDateString(String(cellValue), dateFormat)) {
        return 'Invalid Date Format';
      }
    }

    return 'please complete required cells';
  }

  pluckEmails(hotInstance: Handsontable): string[] {
    const emailColumnIndex = this.columns.findIndex((c) => c.csvHeader === 'invitee_email');
    const data = hotInstance.getData().reduce<string[]>((acc, row: string[], i: number) => {
      if (!hotInstance.isEmptyRow(i) && !isEmpty(row[emailColumnIndex])) {
        acc.push(row[emailColumnIndex]!);
      }
      return acc;
    }, []);
    return data;
  }

  searchEmployeesTask = restartableTask(async (term: string, cb: (names: string[]) => void) => {
    const extraFilters = {
      locations: (await this.location)?.id,
    };
    const employees = await this._searchEmployeesTask.perform(term, extraFilters);
    cb(employees.map((employee) => employee.name));
  });

  @action
  confirmPreregistrationEmailYes(): void {
    this.showPreregistrationEmailConfirmation = false;
    this.notifyVisitor = true;
    void this.submit.perform();
  }

  @action
  confirmPreregistrationEmailNo(): void {
    this.showPreregistrationEmailConfirmation = false;
    void this.submit.perform();
  }

  @action
  modifySecondaryLocations(locations: LocationModel[] | null): void {
    this.args.changeset.childInviteLocations = locations;
  }

  @action
  modifySecondaryLocationOptions(options: SecondaryLocationOption[]): void {
    // Pass through already-selected locations even if their eligibility
    // flipped after selection; only require eligibility for new additions.
    // Filtering the full list silently drops chips on unrelated edits.
    const currentLocations = (this.args.changeset.childInviteLocations ?? []) as LocationModel[];
    const currentIds = new Set(currentLocations.map((loc) => loc.id));
    const accepted = options
      .filter((opt) => currentIds.has(opt.location.id) || opt.eligible)
      .map((opt) => opt.location);
    this.modifySecondaryLocations(accepted);
  }

  @action
  prepareToSubmit(): void {
    if (this.notifyVisitor) {
      void this.submit.perform();
    } else if (this.hasEmail) {
      this.showPreregistrationEmailConfirmation = true;
    } else {
      void this.submit.perform();
    }
  }

  // Excel/macOS pastes embed invisible Unicode that the backend date parser
  // misreads as a day-first date, silently swapping month and day.
  @action
  sanitizePasteData(_hotInstance: Handsontable, data: string[][]): void {
    sanitizeClipboardGrid(data);
  }

  @action
  repairDataFormat(hotInstance: Handsontable, changes: [number, string, string, string][]): void {
    changes.forEach((change) => {
      const [row, prop, _oldValue, newValue] = change;

      if (prop === 'arrivalTime') {
        const timeNoColonRegEx = /^\d{3,4}$/gi;

        if (timeNoColonRegEx.test(`${newValue}`)) {
          const column = hotInstance.propToCol(prop);
          const correctedValue = `${newValue.slice(0, -2)}:${newValue.slice(-2)}`;

          hotInstance.setDataAtCell(row, column, correctedValue, 'arrivalTime');
        }
      } else if (prop === 'arrivalDate') {
        // We need to check the format of the string we get because the call to .setDataAtCell will trigger
        // this to run again, and we don't want to run this on the already-repaired date value.

        const column = hotInstance.propToCol(prop);

        if (ISO8601_DATE_FORMAT.test(newValue)) {
          // On the initial selection of a date in the datepicker, we end up with a full ISO8601 date string
          // because that's what we told momentjs to give us. However, the date is encoded with the LOCATION's
          // time zone, not the current user's time zone, which means that if we just format it as MM/DD/YYYY
          // we could end up showing a day before or after the date that was actually selected. Therefore,
          // we want to convert the date string to a Date object (with the current user's timezone) and then
          // format _that_ as a MM/DD/YYYY value in the table.
          const date = parseISO(newValue);
          const dateStr = format(date, DATE_FNS_MM_DD_YYYY);

          hotInstance.setDataAtCell(row, column, dateStr, 'arrivalDate');
        } else if (/^\d{2}\/\d{2}\/\d{2}$/.test(newValue)) {
          const date = parse(newValue, 'MM/dd/yy', new Date());
          const dateStr = format(date, DATE_FNS_MM_DD_YYYY);

          hotInstance.setDataAtCell(row, column, dateStr, 'arrivalDate');
        }
      }
    });
  }

  @action
  showOrHideConfirmVisitorTypeChangeModal(showOrHide: boolean): void {
    this.showConfirmVisitorTypeChangeModal = showOrHide;
  }

  @action
  hideConfirmVisitorTypeChangeModalAndClear(): void {
    this.modifySecondaryLocations(null);
    this.updateFlow(this.flowNameToChangeTo!);
    this.showOrHideConfirmVisitorTypeChangeModal(false);
  }

  @action
  updateFlowOption(option: FlowOption | null | undefined): void {
    if (!option || !option.eligible) return;
    this.updateFlow(option.flow);
  }

  // After a primary-location switch, scoped-sub data settles asynchronously
  // and may flip the current selection's eligibility from `true` (loading
  // window default) to `false`. Auto-correct to the first eligible option
  // so the picker doesn't show a disabled selection. Fired by a template
  // modifier keyed on `scopedSubsByLocation` changes.
  @action
  reconcileSelectedFlowAfterEligibility(): void {
    if (!this.featureFlags.isEnabled(MPB_FEATURE_FLAG)) return;
    const current = this.selectedFlowOption;
    if (!current || current.eligible) return;
    const firstEligible = this.flowOptions.find((opt) => opt.eligible);
    if (firstEligible && firstEligible.flow.id !== current.flow.id) {
      // System-initiated correction. The auto-selected eligible flow is local
      // (every global flow is ineligible on a non-capable primary), and a
      // local flow can't keep secondaries, so clear them — that also drops
      // updateFlow's confirm-modal guard (secondaryLocations.length) so the
      // flow applies directly. Surface a non-blocking flash so the removal
      // isn't silent, since this path skips the manual confirm modal.
      const hadSecondaries = Boolean(this.args.changeset.childInviteLocations?.length);
      this.modifySecondaryLocations(null);
      if (hadSecondaries) {
        this.flashMessages.showFlash(
          'warning',
          'Additional locations were removed because they aren’t compatible with the new visitor type.',
        );
      }
      this.updateFlow(firstEligible.flow);
    }
  }

  @action
  async handleLocationSelected(location: LocationModel): Promise<void> {
    if (!this.args.onLocationSelectedTask) return;
    try {
      await this.args.onLocationSelectedTask.perform(location);
      // Setting _primaryLocationId fires {{did-update ensureScopedSubsForCandidates ... this._primaryLocationId}}
      // (template.hbs:16), which performs the consolidated [primary, ...siblings]
      // fetch on the restartableTask. A second perform here would cancel that
      // one and leave the primary missing from scopedSubsByLocation (fail-open).
      this._primaryLocationId = location?.id ?? null;
    } catch {
      // Task cancelled or rejected — keep _primaryLocationId consistent
      // with args.changeset.data so flowOptions doesn't promise eligibility
      // for a primary that never loaded.
    }
  }

  @action
  updateFlow(flow: FlowModel): void {
    // Handles reseting fields for Multi-Location Invite option
    if (!this.showConfirmVisitorTypeChangeModal && this.secondaryLocations.length) {
      this.showOrHideConfirmVisitorTypeChangeModal(true);
      this.flowNameToChangeTo = flow;
      return;
    }
    // @ts-expect-error
    setProperties(this, { 'purposeOfVisit.value': flow.name, 'args.changeset.flowName': flow.name });
    // @ts-expect-error
    this.args.changeset.flow = flow;

    this.args.onFlowChanged?.(flow.name, this.args.changeset);
  }
}
